<script setup lang="ts">
import type { ChartData, ChartOptions, LegendOptions } from 'chart.js';
import { useTranslation } from 'i18next-vue';
import {
  computed,
  onMounted,
  ref,
  toRaw,
} from 'vue';

import type {
  DashboardChartItemType,
  IDashboardChartByDayItem,
  IDashboardChartByDayItemData,
  IDashboardChartByMonthsItem,
  IDashboardChartByMonthsItemData,
} from '@modules/dashboard/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VChart from '@components/chart/VChart/VChart.vue';
import VDropdownMenu from '@components/dropdowns/VDropdownMenu/VDropdownMenu.vue';
import {
  useDashboardFilters,
  useDashboardTranslationGroup,
} from '@modules/dashboard/composables';
import { DASHBOARD_CHART_ITEM_TYPES } from '@modules/dashboard/constants';
import { useDashboardChartQuery } from '@modules/dashboard/queries';
import { useI18n } from '@modules/i18n/composables';
import { transformNumber } from '@utils/numbers';

const CHART_HEIGHT_DAY = 500;
const CHART_HEIGHT_MONTH = 200;

const { translationGroup } = useDashboardTranslationGroup();
const { getTranslation } = useI18n();
const { t } = useTranslation();

const { merchantId } = useDashboardFilters();
const { data: dashboardCharts, isFetching } = useDashboardChartQuery(merchantId);

const chartByDayOptions = ref<ChartOptions>();
const chartByMonthOptions = ref<ChartOptions>();

function getChartDataMonth(data: IDashboardChartByMonthsItemData): ChartData {
  const documentStyle = getComputedStyle(document.documentElement);

  const label1 = getTranslation(translationGroup.value, 'CHARTS_BY_MONTH_NEW_PLAYERS').value;
  const label2 = getTranslation(translationGroup.value, 'CHARTS_BY_MONTH_OLD_PLAYERS').value;
  const round = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100;

  return {
    labels: [`${label1}: ${transformNumber(round(data.new_players))}`, `${label2}: ${transformNumber(round(data.old_players))}`],
    datasets: [
      {
        data: [data.new_players, data.old_players],
        backgroundColor: [
          documentStyle.getPropertyValue('--p-primary-color'),
          documentStyle.getPropertyValue('--p-orange-500'),
        ],
        hoverBackgroundColor: [
          documentStyle.getPropertyValue('--p-primary-emphasis'),
          documentStyle.getPropertyValue('--p-orange-400'),
        ],
      },
    ],
  };
}

function getChartOptionsDay(): ChartOptions {
  const documentStyle = getComputedStyle(document.documentElement);
  const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
  const scaleOptions = {
    ticks: {
      color: textColorSecondary,
    },
    grid: {
      color: surfaceBorder,
    },
  };

  return {
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        mode: 'index',
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: { ...scaleOptions },
      y: { ...scaleOptions },
    },
  };
}

const getDatasetXLabels = (): number[] => [...Array.from({ length: new Date().getDate() })].map((e, i) => i + 1);

function getChartDataDay(data: IDashboardChartByDayItemData): ChartData {
  const documentStyle = getComputedStyle(document.documentElement);
  const label1 = getTranslation(translationGroup.value, 'CHARTS_BY_DAY_LAST_MONTH').value;
  const label2 = getTranslation(translationGroup.value, 'CHARTS_BY_DAY_MONTH').value;

  return {
    labels: getDatasetXLabels(),
    datasets: [
      {
        label: label1,
        data: data.last_month,
        tension: 0.4,
        fill: true,
        borderColor: documentStyle.getPropertyValue('--p-orange-500'),
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
      },
      {
        label: label2,
        data: data.month,
        tension: 0.4,
        fill: true,
        borderColor: documentStyle.getPropertyValue('--p-primary-color'),
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
      },
    ],
  };
}

function getChartDropdownOptions(item: IDashboardChartByDayItem | IDashboardChartByMonthsItem, localePath: string): IFormSelectInputOption<DashboardChartItemType>[] {
  return Object.keys(item).map((v) => ({
    value: v as DashboardChartItemType,
    label: t(`${localePath}.${v}`),
  }));
}

const chartDropdownOptionsDay = computed<IFormSelectInputOption<DashboardChartItemType>[]>(() => (
  dashboardCharts.value
    ? getChartDropdownOptions(dashboardCharts.value.chart.by_day, dashboardCharts.value.locale_key_chart)
    : []
));

const chartDropdownOptionsMonth = computed<IFormSelectInputOption<DashboardChartItemType>[]>(() => (
  dashboardCharts.value
    ? getChartDropdownOptions(dashboardCharts.value.chart.by_months, dashboardCharts.value.locale_key_chart)
    : []
));

const chartDataDay = computed(() => dashboardCharts.value?.chart.by_day);
const chartDataMonth = computed(() => dashboardCharts.value?.chart.by_months);

const chartByDayOption = ref<DashboardChartItemType>(DASHBOARD_CHART_ITEM_TYPES[0]);
const chartByMonthOption = ref<DashboardChartItemType>(DASHBOARD_CHART_ITEM_TYPES[0]);

const chartFilteredDataDay = computed(() => {
  const data = chartDataDay?.value?.[chartByDayOption.value];
  if (data) {
    return getChartDataDay(toRaw(data));
  }
  return {};
});

const chartByFilteredDataMonth = computed(() => {
  const data = chartDataMonth?.value?.[chartByMonthOption.value];
  if (data) {
    return getChartDataMonth(toRaw(data));
  }
  return {};
});

function getLabelsOptions(): Partial<LegendOptions<'pie'>['labels']> {
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');

  return {
    color: textColor,
    boxWidth: 15,
    font: {
      family: 'OpenSansRegular, sans-serif',
      size: 14,
    },
  };
}

function getChartOptionsMonth(): ChartOptions {
  return {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { ...getLabelsOptions() },
      },
    },
  };
}

function onChangeByDay(value: DashboardChartItemType): void {
  chartByDayOption.value = value;
}

function onChangeByMonth(value: DashboardChartItemType): void {
  chartByMonthOption.value = value;
}

onMounted(() => {
  chartByDayOptions.value = getChartOptionsDay();
  chartByMonthOptions.value = getChartOptionsMonth();
});
</script>

<template>
  <div v-data-qa-auto-id="'TheDashboardCharts'" class="flex flex-wrap gap-4">
    <VChart
      type="line"
      :data="chartFilteredDataDay"
      :options="chartByDayOptions"
      :title="$getTranslation(translationGroup, 'CHARTS_BY_DAY_HEADER')"
      :height="CHART_HEIGHT_DAY"
      :loading="isFetching"
      class="basis-full lg:basis-2/3"
    >
      <template #dropdown>
        <VDropdownMenu
          name="day"
          :options="chartDropdownOptionsDay"
          :disabled="isFetching"
          :value="chartByDayOption"
          underlined
          style-type="dark"
          @change="onChangeByDay"
        />
      </template>
    </VChart>
    <VChart
      type="pie"
      :data="chartByFilteredDataMonth"
      :options="chartByMonthOptions"
      :title="$getTranslation(translationGroup, 'CHARTS_BY_MONTH_HEADER')"
      :height="CHART_HEIGHT_MONTH"
      :loading="isFetching"
      class="flex-1"
    >
      <template #dropdown>
        <VDropdownMenu
          name="month"
          :options="chartDropdownOptionsMonth"
          :disabled="isFetching"
          underlined
          style-type="dark"
          :value="chartByMonthOption"
          @change="onChangeByMonth"
        />
      </template>
    </VChart>
  </div>
</template>
